import type { Maybe } from '@adornis/base/utilTypes.js';
import type { ChemistryControllerHost } from '@adornis/chemistry/controllers/chemistry-controller.js';
import { Weekday } from '@chancenwerk/enums/enums.js';
import { DateTime, type WeekdayNumbers } from 'luxon';
import type { Product } from '../../_cms/products/entities/Product.js';
import type { Course } from '../../courses/Course.js';
import { CourseAbschluss } from '../../courses/CourseAbschluss.js';
import { CourseDigital } from '../../courses/CourseDigital.js';
import { COURSE_TITLES } from '../../courses/CourseHelper.js';
import { isCourseOfActiveGeneration } from '../../courses/CourseLernfoerderung.js';
import type { GuestDayEventlog } from '../../eventlogs/GuestDayEventlog.js';
import type { PresenceRecord } from '../../PresenceRecord.js';

/** This is how many weeks in the future and past will be pre-generated for selection */
const EVENTS_WEEKS = 8;

const getEffectiveCourseStart = (course: Course) => {
  return (
    course.startDate?.startOf('day') ??
    course.plannedStartDate?.startOf('day') ??
    course.createdAt?.startOf('day') ??
    null
  );
};

const isWithinCourseLifetime = (course: Course, day: DateTime) => {
  const effectiveStart = getEffectiveCourseStart(course);

  if (effectiveStart && day < effectiveStart) {
    return false;
  }

  if (course.archivedAt && day >= course.archivedAt.startOf('day')) {
    return false;
  }

  return true;
};

function buildTitle(course: Course) {
  if (course instanceof CourseAbschluss) {
    return `${COURSE_TITLES[course._class]} ${course.subject} ${course.type}`;
  }

  if (course instanceof CourseDigital) {
    return `${COURSE_TITLES[course._class]}`;
  }

  return `${COURSE_TITLES[course._class]} ${course.location}`;
}

export function generateEvents(
  host: ChemistryControllerHost,
  coursesFromKPF: Course[],
  recordsFromCourses: Record<string, PresenceRecord[]>,
  selectedGuestDays?: GuestDayEventlog[],
  produkt?: Maybe<Product>,
) {
  const existingEvents: any = [];

  for (const id in recordsFromCourses) {
    const course = coursesFromKPF.find(c => c._id === id);
    const records: PresenceRecord[] = recordsFromCourses[id] ?? [];

    if (!course?.weekday) {
      continue;
    }

    records.forEach(r => {
      if (!isCourseOfActiveGeneration(course, produkt, r.date)) {
        return;
      }

      const guestDay = selectedGuestDays?.filter(
        (g: GuestDayEventlog) => g.date?.toFormat('yyyy-MM-dd') === r.date.toFormat('yyyy-MM-dd'),
      );

      existingEvents.push({
        id: `${id}-${r._id}`,
        cid: course._id,
        title: `${buildTitle(course)}${guestDay?.length ? ' (Schnupper-Gast)' : ''}`,
        start: `${r.date.toFormat('yyyy-MM-dd')}T${course.startTime?.toFormat('HH:mm:ss')}`,
        end: `${r.date.toFormat('yyyy-MM-dd')}T${course.endTime?.toFormat('HH:mm:ss')}`,
        backgroundColor: host.colors.accent,
        borderColor: 'transparent',
        display: 'block',
        day: r.date.weekday ? r.date.weekday : -1,
      });
    });
  }

  const start = DateTime.now().startOf('week').minus({ weeks: EVENTS_WEEKS });

  const possibleEvents: any = [];
  for (let w = 0; w < EVENTS_WEEKS * 2 + 1; w++) {
    for (const course of coursesFromKPF) {
      const day = start
        .plus({ weeks: w })
        .set({ weekday: (Object.values(Weekday).indexOf(course.weekday ?? Weekday.MONDAY) + 1) as WeekdayNumbers });

      if (!isWithinCourseLifetime(course, day) || !isCourseOfActiveGeneration(course, produkt, day)) {
        continue;
      }

      const guestDay = selectedGuestDays?.filter(
        (g: GuestDayEventlog) => g.date?.toFormat('yyyy-MM-dd') === day.toFormat('yyyy-MM-dd'),
      );

      const event = {
        id: course._id,
        title: `${buildTitle(course)}${guestDay?.length ? ' (Schnupper-Gast)' : ''}`,
        start: `${day.toFormat('yyyy-MM-dd')}T${course.startTime?.toFormat('HH:mm:ss')}`,
        end: `${day.toFormat('yyyy-MM-dd')}T${course.endTime?.toFormat('HH:mm:ss')}`,
        backgroundColor: host.colors.tone.error.shade(700),
        borderColor: 'transparent',
        display: 'block',
        day: course.weekday ? Object.values(Weekday).indexOf(course.weekday) + 1 : -1,
      };

      if (existingEvents.some(e => e.start === event.start && e.title === event.title && e.cid === course._id)) {
        continue;
      }
      possibleEvents.push(event);
    }
  }

  return [
    // ! I have no clue why but when I remove this event, the calendar doesn't work
    {
      id: 'x-x',
      title: '',
      start: '2000-04-29T13:40:00',
      end: '2000-04-29T15:10:00',
      backgroundColor: {
        raw: {
          r: 0.5490196078431373,
          g: 0.050980392156862744,
          b: 0.03137254901960784,
          a: 1,
        },
        string: '#8c0d08ff',
      },
      borderColor: 'transparent',
      display: 'block',
      day: 2,
    },

    ...existingEvents,
    ...possibleEvents,
  ];
}
